/**
 * EntityRow — the shared row, on both shells.
 *
 * WHY THIS EXISTS. This is the biggest single cohesion win in the 15 September
 * pass. "Something needs you" was drawn two completely different ways: the
 * client portal had one red-left-bordered strip with four facts run together on
 * one 13.5px line, and the firm console had a four-track row with a mono
 * reference and an italic trust name. Same sentence, two products. The console
 * anatomy was the right one, so it becomes the row everywhere and a beneficiary
 * and a compliance officer see the same shape.
 *
 * Contract: docs/redesign-20260915/design_handoff_app_cohesion/README.md,
 * "The shared row".
 *
 * FOUR COLUMNS, in order:
 *   1. reference   mono, nowrap, brass-deep
 *   2. subject     italic-serif entity name over a 12.5px description
 *   3. owner       full queue only
 *   4. urgency     ONE chip, right-aligned, stating the deadline
 *
 * TWO THINGS IT DELIBERATELY DOES NOT DO.
 *
 * It does not take a severity word. The chip says the deadline ("Overdue 6d",
 * "14 days left") and the row carries the severity in its 2px left marker; the
 * console used to render a mono "2d left" AND a separate uppercase "urgent"
 * pill, which is one fact twice at two visual weights.
 *
 * It does not compute anything. No dates, no clocks, no role checks. A caller
 * hands it finished strings, so the FICA clock stays in lib/fica-review.ts
 * where lint:ficaclock can see it, and this file cannot become a second place
 * that decides whether something is overdue.
 *
 * Styling: styles/design-system.css, .entity-row (unscoped, both shells).
 */

import type { MouseEvent, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

/** How urgent, which decides the left marker and the chip tone. */
export type RowUrgency = 'overdue' | 'soon' | 'routine' | 'done';

export interface EntityRowProps {
  /**
   * The mono reference: RES-0412, DSAR-118, FICA-REV. Omit when the thing has
   * no reference a user would ever quote; the column then collapses.
   */
  reference?: string;
  /**
   * The named thing: a trust, a person, a firm. Rendered italic serif, which in
   * this product means exactly one thing, that this is an entity you can open.
   */
  name: string;
  /** The 12.5px line under the name. What it is, or what it is waiting on. */
  description?: ReactNode;
  /** Whose it is. Rendered only on the four-track queue layout. */
  owner?: ReactNode;
  /**
   * The urgency chip text, which STATES THE DEADLINE: "Overdue 6d",
   * "2 days left", "Signed 14 Aug". Sentence case. Omit for a routine row that
   * is not waiting on anything.
   */
  urgency?: string;
  /** Drives the left marker and the chip tone. Defaults to routine. */
  tone?: RowUrgency;
  /**
   * Where the row goes. A row is a link to the entity and the WHOLE row is the
   * target. Omit for a row that is not navigable; it then renders as a div and
   * takes no hover affordance it cannot honour.
   */
  to?: string;
  /** Route params for `to`, passed straight to the router. */
  params?: Record<string, string>;
  /** Search params for `to`, passed straight to the router. */
  search?: Record<string, unknown>;
  /** Buttons at the row end. They stopPropagation so the row link still works. */
  actions?: ReactNode;
  /** `queue` is the four-track full-queue layout; `card` is the default three. */
  layout?: 'card' | 'queue';
  /**
   * ADDITIVE, cohesion pass phase 4 (2026-09-15).
   *
   * What `name` IS. Italic serif in this product means one thing, a named
   * entity, so a row whose subject line is not one (a message subject, a
   * document title) must not borrow the treatment or the rule stops carrying
   * meaning. `text` renders the same slot as functional sans at row-primary
   * weight. Defaults to `entity`, which is what every existing caller means.
   */
  subjectKind?: 'entity' | 'text';
  /**
   * ADDITIVE, cohesion pass phase 4 (2026-09-15), for the inbox thread list.
   *
   * A row that SELECTS rather than navigates. The inbox's thread list is a
   * master/detail pair on one route, so its rows change what the pane beside
   * them shows instead of going anywhere, and they were the last hand-rolled
   * row anatomy in the portal. Given `onClick` and no `to`, the row renders a
   * real `<button>`: keyboard-reachable, announced as a control, and not an
   * anchor pretending to be one. `to` still wins if both are passed, because a
   * row that navigates is a link and nothing else.
   */
  onClick?: () => void;
  /**
   * ADDITIVE, same pass. This row is the one currently open in the detail pane.
   * It takes the brass left marker and the hover ground permanently, which is
   * the contract's own treatment for a selected thread, and `aria-current` so
   * the selection is not carried by colour alone.
   */
  selected?: boolean;
  /**
   * ADDITIVE, same pass. Content for the first track when the row has no mono
   * reference: an avatar, an initials roundel, a glyph. Ignored when
   * `reference` is set, so the row can never grow a second first column.
   */
  leading?: ReactNode;
  /** Test id suffix, so a screen with two lists can tell them apart. */
  testId?: string;
}

const CHIP_TONE: Record<RowUrgency, string> = {
  overdue: ' entity-row-chip--overdue',
  soon: ' entity-row-chip--soon',
  done: ' entity-row-chip--done',
  routine: '',
};

const MARKER: Record<RowUrgency, string> = {
  overdue: ' entity-row--overdue',
  soon: ' entity-row--soon',
  done: '',
  routine: '',
};

export function EntityRow({
  reference,
  name,
  description,
  owner,
  urgency,
  tone = 'routine',
  to,
  params,
  search,
  actions,
  layout = 'card',
  subjectKind = 'entity',
  onClick,
  selected = false,
  leading,
  testId,
}: EntityRowProps) {
  const className =
    'entity-row' +
    (layout === 'queue' ? ' entity-row--queue' : '') +
    /* A selected row keeps the brass marker whatever its urgency, because
       "this is the one you are reading" is the more useful fact of the two and
       two markers on one edge is not a thing. */
    (selected ? ' entity-row--selected' : MARKER[tone]);

  const body = (
    <>
      {reference ? (
        <span className="entity-row-ref">{reference}</span>
      ) : leading ? (
        <span className="entity-row-lead">{leading}</span>
      ) : null}
      <span className="entity-row-subject">
        <span
          className={subjectKind === 'entity' ? 'entity-row-name' : 'entity-row-primary'}
        >
          {name}
        </span>
        {description ? <span className="entity-row-desc">{description}</span> : null}
      </span>
      {layout === 'queue' && owner ? <span className="entity-row-owner">{owner}</span> : null}
      {urgency ? (
        <span className={`entity-row-chip${CHIP_TONE[tone]}`}>{urgency}</span>
      ) : null}
      {actions ? (
        /* The row is a link; a button inside it must not also follow the link.
         * Doing it here rather than on each caller's button means a screen
         * cannot forget to, which is how a Decline button ships navigating
         * away instead of declining.
         *
         * BOTH calls are needed and neither is enough on its own.
         * stopPropagation keeps the router's own click handler on the anchor
         * from running, and preventDefault stops the BROWSER following the
         * anchor's href, which stopPropagation does not touch: a cancelled
         * propagation with a live default action is a full page load.
         * Safe for the children, because React dispatches its synthetic click
         * from the target outward, so the button's own onClick has already run
         * by the time this fires, and a type="button" button has no default
         * action of its own to lose. */
        <span
          className="entity-row-actions"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {actions}
        </span>
      ) : null}
    </>
  );

  const shared = {
    className,
    'data-testid': testId ? `entity-row-${testId}` : 'entity-row',
    ...(selected ? { 'aria-current': true as const } : {}),
  };

  if (!to) {
    if (onClick) {
      return (
        <button type="button" {...shared} onClick={onClick}>
          {body}
        </button>
      );
    }
    return <div {...shared}>{body}</div>;
  }

  return (
    <Link
      {...shared}
      to={to}
      {...(params ? { params } : {})}
      {...(search ? { search } : {})}
    >
      {body}
    </Link>
  );
}
